import React, { useState, useEffect, useRef } from 'react'
import { Header, LayoutContainer, NavBar } from '../../components'
import { useTranslation } from 'react-i18next'
import { gqlOrderDelivery, gqlUsers } from '../../gql'
import { useQuery, useMutation } from '@apollo/client'
import dayjs from 'dayjs'
import { formatInTimeZone } from 'date-fns-tz'
import 'dayjs/locale/fr'
import { STATUSES } from '../../lib/constants'
import { getUpdatedStatus } from '../../lib/command'
import { send, listen } from '../../lib/bus'
import api from '../../api'
import fileDownload from 'js-file-download'
import PlanningPageInPage from './PlanningPageInPage'
import PlanningPageFullScreen from './PlanningPageFullScreen'

dayjs.locale('fr')

const getWeekDays = (selectedDate) => {
  const startOfWeek = selectedDate.startOf('week')
  return Array.from({ length: 5 }).map((_, i) => startOfWeek.add(i, 'day'))
}

function PlanningPage() {
  const { t } = useTranslation('planning_page')
  const { data: roles, error: errorRoles, loading: loadingRoles } = useQuery(gqlUsers.ROLES)
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const [isFullScreen, setIsFullScreen] = useState(false)
  const getNextBusinessDay = (date) => {
    let nextDate = date.add(1, 'day')
    if (nextDate.day() === 6) {
      nextDate = nextDate.add(2, 'day')
    } else if (nextDate.day() === 0) {
      nextDate = nextDate.add(1, 'day')
    }
    return nextDate
  }
  const [selectedDate, setSelectedDate] = useState(getNextBusinessDay(dayjs()))
  const [weekDays, setWeekDays] = useState(getWeekDays(selectedDate))
  const [drivers, setDrivers] = useState([])
  const [isPlanificationSentAtLeastOnce, setIsPlanningSentAtLeastOnce] = useState(false)
  const [createPlanification] = useMutation(gqlOrderDelivery.CREATE_PLANIFICATION)
  const today = dayjs().startOf('day')
  const isPastDate = selectedDate.isBefore(today)
  const nextBusinessDay = getNextBusinessDay(today)

  const isTodayOrNextBusinessDay =
    selectedDate.isSame(today, 'day') || selectedDate.isSame(nextBusinessDay, 'day')
  useEffect(() => {
    setWeekDays(getWeekDays(selectedDate))
  }, [selectedDate])

  const driverPermanentId = roles?.roles.nodes.find(
    (role) => role.name === 'bdn_driver_permanent'
  )?.displayName
  const driverOccasionalId = roles?.roles.nodes.find(
    (role) => role.name === 'bdn_driver_occasional'
  )?.displayName

  const {
    data: permanentDrivers,
    error: errorUsersPermanent,
    loading: loadingUsersPermanent,
    refetch: refetch_driverPermanent,
  } = useQuery(gqlUsers.USERS_PLANNING, {
    variables: {
      companyRole: driverPermanentId ? [driverPermanentId] : [],
      startDate: selectedDate.format('YYYY-MM-DD'),
      endDate: selectedDate.add(1, 'day').format('YYYY-MM-DD'),
      skip: !driverPermanentId,
    },
  })

  const {
    data: occasionalDrivers,
    error: errorUsersOccasional,
    loading: loadingUsersOccasional,
    refetch: refetch_driverOccasional,
  } = useQuery(gqlUsers.USERS_PLANNING, {
    variables: {
      companyRole: driverOccasionalId ? [driverOccasionalId] : [],
      startDate: selectedDate.format('YYYY-MM-DD'),
      endDate: selectedDate.add(1, 'day').format('YYYY-MM-DD'),
      deliveryWorkDate: selectedDate.format('YYYY-MM-DD'),
      skip: !driverOccasionalId,
    },
  })

  const queryVariables = {
    startDate: selectedDate.format('YYYY-MM-DD'),
    endDate: selectedDate.add(1, 'day').format('YYYY-MM-DD'),
    search: null,
    searchNoSpaces: null,
  }

  const {
    data: ordersDelivery,
    error: errorOrdersDelivery,
    loading: loadingOrdersDelivery,
    refetch,
  } = useQuery(gqlOrderDelivery.ORDER_DELIVERIES, {
    variables: queryVariables,
  })

  const { data: planificationDates, refetch: refetch_planification } = useQuery(
    gqlOrderDelivery.DATES_PLANIFICATION
  )

  const [updateOrderDelivery] = useMutation(gqlOrderDelivery.UPDATE_ORDER_DELIVERY)
  const [createDriverOutOfOffice] = useMutation(gqlUsers.CREATE_DRIVER_OUT_OF_OFFICE)
  const [deleteDriverOutOfOffice] = useMutation(gqlUsers.DELETE_DRIVER_OUT_OF_OFFICE)

  const loading =
    loadingUsersPermanent || loadingUsersOccasional || loadingOrdersDelivery || loadingRoles
  const error = errorUsersPermanent || errorUsersOccasional || errorOrdersDelivery || errorRoles

  const isDateInArray = (dates, selectedDate) => {
    return dates.some((item) => dayjs(item.sentPlanificationDate).isSame(selectedDate, 'day'))
  }

  useEffect(() => {
    const dates = planificationDates?.planifications?.nodes || []
    if (dates.length && isDateInArray(dates, selectedDate)) {
      setIsPlanningSentAtLeastOnce(true)
    } else {
      setIsPlanningSentAtLeastOnce(false)
    }
  }, [planificationDates, selectedDate])

  useEffect(() => {
    const combinedDrivers = [
      ...(permanentDrivers?.users?.nodes || []),
      ...(occasionalDrivers?.users?.nodes || []),
    ].filter((d) => d.isActive !== false).map((driver) => {
      const absences = driver.driverOutOfOffices?.nodes || []
      return {
        ...driver,
        isOutOfOfficeToday: absences.length > 0,
        outOfOfficeId: absences.length > 0 ? absences[0].id : null,
      }
    }).sort((a, b) => {
      const tc = a.transporter.name.localeCompare(b.transporter.name)
      if (tc !== 0) return tc
      const lc = a.lastname.localeCompare(b.lastname)
      if (lc !== 0) return lc
      return a.firstname.localeCompare(b.firstname)
    })
    if (combinedDrivers.length) {
      setDrivers(combinedDrivers)
    } else {
      setDrivers([])
    }
  }, [permanentDrivers, occasionalDrivers])

  const [orders, setOrders] = useState(ordersDelivery)
  useEffect(() => {
    setOrders(ordersDelivery)
  }, [ordersDelivery])

  const refetchAllData = async () => {
    const commonVariables = {
      startDate: selectedDate.format('YYYY-MM-DD'),
      endDate: selectedDate.add(1, 'day').format('YYYY-MM-DD'),
    }
    await refetch_driverPermanent({
      companyRole: driverPermanentId ? [driverPermanentId] : [],
      ...commonVariables,
    })
    await refetch_driverOccasional({
      companyRole: driverOccasionalId ? [driverOccasionalId] : [],
      ...commonVariables,
      deliveryWorkDate: selectedDate.format('YYYY-MM-DD'),
    })
    await refetch({
      ...commonVariables,
      search: null,
      searchNoSpaces: null,
    })
  }

  const refetchAllDataRef = useRef(refetchAllData)
  refetchAllDataRef.current = refetchAllData
  const setSelectedDateRef = useRef(setSelectedDate)
  setSelectedDateRef.current = setSelectedDate
  useEffect(() => {
    listen((data) => {
      if (data.type === 'planning:updated') {
        refetchAllDataRef.current()
      }
      if (data.type === 'date:changed') {
        setSelectedDateRef.current(dayjs(data.payload.date))
      }
    })
  }, [])

  const updateOrderStatusAndUserID = async (orderToUpdate, userId, statusFlag) => {
    const updatedStatus = getUpdatedStatus(
      orderToUpdate.node,
      orderToUpdate.node.command,
      orderToUpdate.node.orderDeliveryMaterials.nodes,
      statusFlag
    )
    await updateOrderDelivery({
      variables: { ...orderToUpdate.node, userId: userId, status: updatedStatus },
    })
  }

  const toggleDriverStatus = async (driverId) => {
    const driverToUpdate = drivers.find((user) => user.id === driverId)
    const ordersToUpdate = orders.orderDeliveries.edges.filter(
      (order) => order.node.userId === driverId
    )
    try {
      if (driverToUpdate.isOutOfOfficeToday) {
        await deleteDriverOutOfOffice({
          variables: { id: driverToUpdate.outOfOfficeId },
        })
      } else {
        await createDriverOutOfOffice({
          variables: {
            userId: driverId,
            oooDate: selectedDate.format('YYYY-MM-DD'),
          },
        })
        for (const order of ordersToUpdate) {
          await updateOrderStatusAndUserID(order, null, false)
        }
      }
      await refetchAllData()
      send('planning:updated')
    } catch (error) {
      console.error('Error updating driver absence:', error)
    }
  }

  const moveOrder = async (order, toDriverId, fromDriverId) => {
    const orderToUpdate = orders.orderDeliveries.edges.find(
      (newOrder) => newOrder.node.id === order.id
    )
    if (isPastDate || !orderToUpdate || orderToUpdate.node.status === STATUSES.VALID) return

    try {
      await updateOrderStatusAndUserID(orderToUpdate, toDriverId, true)
      await refetchAllData()
      send('planning:updated')
      if (fromDriverId) {
        sendNotificationUpdatedOrder(orderToUpdate, fromDriverId)
      }
      if (toDriverId) {
        sendNotificationUpdatedOrder(orderToUpdate, toDriverId)
      }
    } catch (error) {
      console.error('Error updating orders:', error)
    }
  }

  const moveOrderToList = async (order) => {
    const orderToUpdate = orders.orderDeliveries.edges.find(
      (newOrder) => newOrder.node.id === order.id
    )
    if (isPastDate || !orderToUpdate || orderToUpdate.node.status === STATUSES.VALID) return

    try {
      await updateOrderStatusAndUserID(orderToUpdate, null, false)
      await refetchAllData()
      send('planning:updated')
      if (order.fromDriverId) {
        sendNotificationUpdatedOrder(orderToUpdate, order.fromDriverId)
      }
    } catch (error) {
      console.log(error)
    }
  }

  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleAddOccasionalDriver = () => {
    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
  }

  const handleRefreshPlanning = async () => {
    try {
      await refetch_driverOccasional()
      send('planning:updated')
    } catch (error) {
      console.error('Error refreshing list:', error)
    }
  }

  const handleExportExcel = async () => {
    try {
      const dateStr = selectedDate.format('YYYY-MM-DD')
      const blob = await api.exportPlanning(dateStr)
      fileDownload(blob, `planning_${dateStr}.xlsx`)
    } catch (error) {
      console.error("Erreur lors de l'export Excel:", error)
    }
  }

  const handleSendPlanification = () => {
    setIsConfirmationOpen(true)
  }

  const updateOrderDeliveries = async () => {
    const ordersToUpdate = orders.orderDeliveries.edges.filter(
      (edge) => edge.node.status === STATUSES.PLANIFIED
    )

    for (const order of ordersToUpdate) {
      try {
        await updateOrderDelivery({
          variables: {
            id: order.node.id,
            isPlanificationSent: true,
            status: order.node.status,
            deliveryDate: order.node.deliveryDate,
            deliveryTimeStart: order.node.deliveryTimeStart,
            deliveryTimeEnd: order.node.deliveryTimeEnd,
          },
        })
      } catch (error) {
        console.error(`Failed to update order ${order.node.id}:`, error)
      }
    }
  }

  const handleConfirmSend = async () => {
    try {
      const response = await api.sendPlanning()
      if (response.success) {
        updateOrderDeliveries()
        await createPlanification({
          variables: {
            sentPlanificationDate: formatInTimeZone(
              new Date(selectedDate),
              'Europe/Paris',
              "yyyy-MM-dd'T'HH:mm:ssXXX"
            ),
          },
        })
        await refetch_planification()
        send('planning:updated')
      } else {
        console.error("Erreur lors de l'envoi du planning : ", response.error || 'Unknown error')
      }
    } catch (error) {
      console.error("Erreur inattendue lors de l'envoi du planning : ", error)
    } finally {
      setIsConfirmationOpen(false)
    }
  }

  function sendNotificationUpdatedOrder(orderToUpdate, driverId) {
    if (
      orderToUpdate?.node?.isPlanificationSent &&
      (orderToUpdate?.node?.status === STATUSES.PLANIFIED ||
        orderToUpdate?.node?.status === STATUSES.COMPLETE) &&
      driverId
    ) {
      api.sendNotification(driverId).catch(() => {
        console.error("Erreur lors de l'envoi de la notification au chauffeur avec l'ID", driverId)
      })
    }
  }

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
      setIsFullScreen(true)
    } else {
      document.exitFullscreen()
      setIsFullScreen(false)
    }
  }

  useEffect(() => {
    const handleFSChange = () => {
      if (!document.fullscreenElement) {
        setIsFullScreen(false)
      }
    }
    document.addEventListener('fullscreenchange', handleFSChange)
    return () => document.removeEventListener('fullscreenchange', handleFSChange)
  }, [])

  const inPageProps = {
    selectedDate,
    setSelectedDate,
    weekDays,
    drivers,
    isPlanificationSentAtLeastOnce,
    isPastDate,
    isTodayOrNextBusinessDay,
    loading,
    error,
    orders,
    isModalOpen,
    handleModalClose,
    handleAddOccasionalDriver,
    handleRefreshPlanning,
    handleSendPlanification,
    handleConfirmSend,
    setIsConfirmationOpen,
    isConfirmationOpen,
    moveOrder,
    moveOrderToList,
    toggleDriverStatus,
    toggleFullScreen,
    onExportExcel: handleExportExcel,
  }

  if (isFullScreen) {
    return <PlanningPageFullScreen {...inPageProps} />
  }

  return (
    <div className="planning">
      <Header title={t('title')} />
      <NavBar active="planning" />
      <LayoutContainer>
        <PlanningPageInPage {...inPageProps} />
      </LayoutContainer>
    </div>
  )
}

export default PlanningPage
